import { useEffect } from "react";

/**
 * Calls `handler` when the Escape key is pressed. Used to make full-screen
 * sub-pages dismiss/go-back with Esc, mirroring the Esc-to-close behavior of
 * dialogs.
 *
 * Notes:
 * - Ignores Esc while the user is typing in an input, textarea, or
 *   contenteditable, so it doesn't hijack "cancel field" gestures.
 * - Ignores events already handled elsewhere (e.g. a dialog that closed on Esc
 *   and called `preventDefault`), so an open modal takes precedence over the
 *   page's back handler.
 */
export function useEscapeKey(handler?: () => void, enabled: boolean = true): void {
  useEffect(() => {
    if (!enabled || typeof handler !== "function") return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || e.defaultPrevented) return;
      const t = e.target as HTMLElement | null;
      if (
        t &&
        (t.tagName === "INPUT" ||
          t.tagName === "TEXTAREA" ||
          t.tagName === "SELECT" ||
          t.isContentEditable)
      ) {
        return;
      }
      handler();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [handler, enabled]);
}
